import { useEffect, useRef, useState, type FormEvent } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { getAppLogoSrc } from '../../constants/branding';
import { resolveTenantProductName, resolveThemeAwareCompactLogo, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { formatShortTicketRef } from '../../lib/ticketRef';
import {
  createSupportTicket,
  sendSupportChat,
  type SupportChatEscalationReason,
  type SupportChatRequestError,
  type SupportChatWireMessage,
} from '../../services/supportChatApi';

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
};

const SUGGESTED_PROMPTS = [
  'Import a CSV',
  'Rejected rows',
  'P&L differences',
  'Replay execution',
];

const buildGreetingText = () => `Hi! How can I help you with ${resolveTenantProductName()} today?`;

// Perceived-response timing for every assistant reply (and the local greeting):
//   1. 3s silence — no typing indicator visible.
//   2. Typing indicator appears.
//   3. Typing stays visible at least 4s.
//   4. Reply appears.
// Total minimum perceived response time is 7 seconds.
const TYPING_START_DELAY_MS = 3000;
const MIN_TYPING_VISIBLE_MS = 4000;
const TOTAL_MIN_RESPONSE_MS = TYPING_START_DELAY_MS + MIN_TYPING_VISIBLE_MS;

// Matches a first message that is only a simple greeting (optionally with
// trailing punctuation/whitespace). Anything else is treated as a real question.
const SIMPLE_GREETING_RE =
  /^(hi|hello|hey|howdy|hiya|yo|sup|greetings|good\s+(morning|afternoon|evening)|(hi|hello|hey)\s+there)[\s!.,~^]*$/i;

const isSimpleGreeting = (text: string) => SIMPLE_GREETING_RE.test(String(text || '').trim());

// Derives a concise issue summary for a support ticket from the conversation,
// using the last user question.
const buildTicketSummary = (transcript: SupportChatWireMessage[]) => {
  const lastUser = [...transcript].reverse().find((message) => message.role === 'user');
  const text = String(lastUser?.content || '').trim();
  return text.slice(0, 280) || 'Support request from the in-app chat assistant.';
};

// Short affirmative replies that confirm the pending "create a ticket?" prompt.
// When one of these arrives while a ticket prompt is active, the widget creates
// the ticket through the real API instead of letting the model claim success.
// Matching is case-insensitive and tolerates surrounding punctuation and extra
// whitespace. These phrases only count as confirmations while an escalation
// prompt is active (see send()), so ambiguous uses in a normal conversation
// stay ordinary chat messages.
const TICKET_CONFIRMATION_PHRASES = new Set([
  'yes',
  'yeah',
  'yep',
  'yup',
  'sure',
  'sure thing',
  'please',
  'yes please',
  'ok',
  'okay',
  'okey',
  'absolutely',
  'definitely',
  'of course',
  'certainly',
  'confirm',
  'confirmed',
  'please confirm',
  'i confirm',
  'i confirm it',
  'go ahead',
  'go for it',
  'please do',
  'please do it',
  'do it',
  'do it please',
  'proceed',
  'please proceed',
  'submit',
  'submit it',
  'submit the ticket',
  'submit a ticket',
  'create it',
  'create the ticket',
  'create a ticket',
  'sounds good',
  'sounds great',
  'sounds good to me',
  'that sounds good',
  'yes go ahead',
  'yes do it',
  'yes please do it',
  'ok go ahead',
  'ok do it',
  'ok sure',
]);

// Normalizes a typed confirmation for matching: lowercases the text, strips
// surrounding punctuation/whitespace, and collapses internal whitespace so
// "Go ahead!!" and "go  ahead" both match "go ahead".
const normalizeConfirmationText = (rawText: string) =>
  String(rawText || '')
    .trim()
    .toLowerCase()
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

const isTicketConfirmation = (rawText: string) => {
  const normalized = normalizeConfirmationText(rawText);
  if (TICKET_CONFIRMATION_PHRASES.has(normalized)) return true;
  // Accept comma/"and"-joined combinations such as "ok, go ahead".
  const parts = normalized.split(/\s*(?:,|\band\b)\s*/);
  return parts.length > 1 && parts.every((part) => TICKET_CONFIRMATION_PHRASES.has(part));
};

// Negative replies that decline the pending "create a ticket?" prompt. When one
// of these arrives while a ticket prompt is active, the widget drops the prompt
// without creating a ticket and acknowledges locally. Like confirmations, these
// only count as cancellations while an escalation prompt is active.
const TICKET_CANCELLATION_PHRASES = new Set([
  'no',
  'nope',
  'nah',
  'no thanks',
  'no thank you',
  'not now',
  'not today',
  'not right now',
  'cancel',
  'cancel it',
  'cancel the ticket',
  'never mind',
  'nevermind',
  'never mind it',
  "don't",
  'dont',
  "don't bother",
  'do not',
  'do not create',
  "don't create it",
  "don't create the ticket",
  'do not create it',
  'do not create the ticket',
  'stop',
  'stop it',
  'skip',
  'skip it',
  'forget it',
  'forget about it',
]);

const isTicketCancellation = (rawText: string) => {
  const normalized = normalizeConfirmationText(rawText);
  if (TICKET_CANCELLATION_PHRASES.has(normalized)) return true;
  // Accept comma/"and"-joined combinations such as "no, stop".
  const parts = normalized.split(/\s*(?:,|\band\b)\s*/);
  return parts.length > 1 && parts.every((part) => TICKET_CANCELLATION_PHRASES.has(part));
};

// Deterministic ticket-creation copy shown only after the ticket API confirms
// persistence — the model must never claim a ticket was created on its own.
const TICKET_CREATED_SUCCESS = (ticketId: string) =>
  `Got it — your support ticket has been created. Ticket #${formatShortTicketRef(ticketId)}.`;
const TICKET_CANCELLED_MESSAGE =
  "No problem. I won't create a ticket. If you need anything else, I'm here.";

type EscalationPrompt = {
  reason: SupportChatEscalationReason | null;
  transcript: SupportChatWireMessage[];
  /** AI-generated concise subject; falls back to the raw transcript when absent. */
  ticketSubject: string | null;
};

type TicketStatus =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'created'; ticketId: string }
  | { kind: 'error' };

const FRIENDLY_ERROR_MESSAGE =
  "Sorry, I couldn't reach the assistant just now. Please try again in a moment.";
const RATE_LIMIT_MESSAGE = 'Please wait a moment before sending another message.';
const FALLBACK_RATE_LIMIT_COOLDOWN_MS = 60_000;

/**
 * Support-page-only floating AI chat widget.
 *
 * Rendered from SupportPage, so it only exists in the DOM while that page is
 * mounted. Conversation history is posted to POST /api/support/chat, which
 * calls DeepSeek server-side and returns the assistant reply. Typing/loading
 * state stays client-side; failures show a friendly retry banner.
 */
export default function AIChatWidget({ whiteLabel = null }: { whiteLabel?: WhiteLabelAppBranding | null }) {
  const [isOpen, setIsOpen] = useState(false);
  const isDarkTheme = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const logoTheme = isDarkTheme ? 'dark' : 'light';
  const tenantLogoSrc = resolveThemeAwareCompactLogo(whiteLabel, logoTheme)
    || getAppLogoSrc({ variant: 'symbol', theme: logoTheme });
  const [draft, setDraft] = useState('');
  // `isTyping` tracks an in-flight request (disables input and blocks
  // double-sends). The *visual* typing indicator is tracked by `showTyping`,
  // which is delayed by TYPING_START_DELAY_MS so it never flashes for fast replies.
  const [isTyping, setIsTyping] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [escalationPrompt, setEscalationPrompt] = useState<EscalationPrompt | null>(null);
  const [ticketStatus, setTicketStatus] = useState<TicketStatus>({ kind: 'idle' });
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messageIdRef = useRef(0);
  const failedMessagesRef = useRef<ChatMessage[] | null>(null);
  // Guards against double-submitting ticket creation when two confirmations
  // land before React state has settled (e.g. Enter + click in the same tick).
  const ticketInFlightRef = useRef(false);
  const greetingTimerRef = useRef<number | null>(null);
  const typingShowTimerRef = useRef<number | null>(null);
  const rateLimitTimerRef = useRef<number | null>(null);
  const submissionLockRef = useRef(false);

  const isRateLimited = rateLimitUntil !== null && rateLimitUntil > Date.now();

  const startRateLimitCooldown = (error: SupportChatRequestError) => {
    if (rateLimitTimerRef.current !== null) window.clearTimeout(rateLimitTimerRef.current);
    const cooldownMs = Math.max(1000, error.retryAfterMs ?? FALLBACK_RATE_LIMIT_COOLDOWN_MS);
    const until = Date.now() + cooldownMs;
    setRateLimitUntil(until);
    rateLimitTimerRef.current = window.setTimeout(() => {
      rateLimitTimerRef.current = null;
      setRateLimitUntil(null);
    }, cooldownMs);
  };

  useEffect(() => {
    return () => {
      if (greetingTimerRef.current !== null) {
        window.clearTimeout(greetingTimerRef.current);
      }
      if (typingShowTimerRef.current !== null) {
        window.clearTimeout(typingShowTimerRef.current);
      }
      if (rateLimitTimerRef.current !== null) {
        window.clearTimeout(rateLimitTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [messages, isTyping, showTyping]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const toWireMessages = (history: ChatMessage[]): SupportChatWireMessage[] =>
    history
      .filter((message) => message.id !== 'greeting')
      .map((message) => ({ role: message.role, content: message.text }));

  const scheduleTypingIndicator = () => {
    if (typingShowTimerRef.current !== null) {
      window.clearTimeout(typingShowTimerRef.current);
    }
    typingShowTimerRef.current = window.setTimeout(() => {
      typingShowTimerRef.current = null;
      // Replies are always held for at least TOTAL_MIN_RESPONSE_MS, so the request
      // is still pending here; reveal the indicator only once the user is waiting.
      setShowTyping(true);
    }, TYPING_START_DELAY_MS);
  };

  const waitRemainingDisplayTime = async (startedAt: number, minDisplayMs: number) => {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, minDisplayMs - elapsed);
    if (remaining > 0) {
      await new Promise<void>((resolve) => window.setTimeout(resolve, remaining));
    }
  };

  const requestReply = async (history: ChatMessage[]) => {
    const ticketOptionWasVisible = Boolean(escalationPrompt);
    // Start the API request immediately; hold the reply until the full 7s
    // perceived-response sequence completes, unless the API was slower.
    const startedAt = Date.now();
    try {
      const result = await sendSupportChat(toWireMessages(history));
      await waitRemainingDisplayTime(startedAt, TOTAL_MIN_RESPONSE_MS);
      // Once the option is visible, the escalation introduction has already
      // been shown. Keep subsequent turns as normal chat without repeating it.
      if (!(ticketOptionWasVisible && result.escalate)) {
        const nextId = ++messageIdRef.current;
        setMessages((current) => [
          ...current,
          { id: `assistant-${nextId}`, role: 'assistant', text: result.reply },
        ]);
      }
      failedMessagesRef.current = null;
      // Only offer ticket creation once enough issue detail has been collected.
      if (result.escalate) {
        setEscalationPrompt({
          reason: result.escalationReason || null,
          transcript: toWireMessages(history),
          ticketSubject: result.ticketSubject || null,
        });
        setTicketStatus({ kind: 'idle' });
      } else {
        // A visible ticket option remains available while the user continues
        // troubleshooting; it is dismissed only by Cancel or ticket creation.
        if (!ticketOptionWasVisible) setEscalationPrompt(null);
      }
    } catch (error) {
      await waitRemainingDisplayTime(startedAt, TOTAL_MIN_RESPONSE_MS);
      failedMessagesRef.current = history;
      if ((error as SupportChatRequestError)?.status === 429) {
        setErrorMessage(null);
        startRateLimitCooldown(error as SupportChatRequestError);
      } else {
        setErrorMessage(FRIENDLY_ERROR_MESSAGE);
      }
    } finally {
      setIsTyping(false);
      setShowTyping(false);
      submissionLockRef.current = false;
    }
  };

  const handleCreateTicket = async (announceInChat = false) => {
    if (!escalationPrompt || ticketStatus.kind === 'creating' || ticketInFlightRef.current) return;
    ticketInFlightRef.current = true;
    setTicketStatus({ kind: 'creating' });
    try {
      const { ticketId } = await createSupportTicket({
        // Prefer the AI-generated subject; fall back to the raw conversation.
        summary: escalationPrompt.ticketSubject || buildTicketSummary(escalationPrompt.transcript),
        escalationReason: escalationPrompt.reason,
        transcript: escalationPrompt.transcript,
      });
      if (announceInChat) {
        const nextId = ++messageIdRef.current;
        setMessages((current) => [
          ...current,
          { id: `assistant-${nextId}`, role: 'assistant', text: TICKET_CREATED_SUCCESS(ticketId) },
        ]);
      }
      setEscalationPrompt(null);
      setTicketStatus({ kind: 'created', ticketId });
    } catch {
      if (announceInChat) {
        const nextId = ++messageIdRef.current;
        setMessages((current) => [
          ...current,
          { id: `assistant-${nextId}`, role: 'assistant', text: "I couldn't create the ticket just now. Please try again." },
        ]);
      }
      setTicketStatus({ kind: 'error' });
    } finally {
      ticketInFlightRef.current = false;
    }
  };

  /**
   * Drops the pending ticket prompt without creating a ticket and shows a short
   * acknowledgement. Used by the Cancel button and typed cancellations.
   */
  const cancelTicketPrompt = () => {
    if (!escalationPrompt) return;
    setEscalationPrompt(null);
    setTicketStatus({ kind: 'idle' });
    const nextId = ++messageIdRef.current;
    setMessages((current) => [
      ...current,
      { id: `assistant-${nextId}`, role: 'assistant', text: TICKET_CANCELLED_MESSAGE },
    ]);
  };

  const scheduleGreetingReply = () => {
    greetingTimerRef.current = window.setTimeout(() => {
      greetingTimerRef.current = null;
      setMessages((current) => [
        ...current,
        { id: 'greeting', role: 'assistant', text: buildGreetingText() },
      ]);
      setIsTyping(false);
      setShowTyping(false);
      submissionLockRef.current = false;
    }, TOTAL_MIN_RESPONSE_MS);
  };

  const send = (rawText: string) => {
    const text = rawText.trim();
    const ticketAction = Boolean(escalationPrompt && (isTicketConfirmation(text) || isTicketCancellation(text)));
    if (!text || isTyping || submissionLockRef.current || (isRateLimited && !ticketAction)) return;
    submissionLockRef.current = true;

    const nextId = ++messageIdRef.current;
    const userMessage: ChatMessage = { id: `user-${nextId}`, role: 'user', text };
    setMessages((current) => [...current, userMessage]);
    setDraft('');
    setErrorMessage(null);

    // When a ticket prompt is pending and the user confirms, create the ticket
    // through the real API before any success wording is shown. This prevents
    // the model from claiming a ticket was created without persistence.
    if (escalationPrompt && isTicketConfirmation(text)) {
      void handleCreateTicket(true);
      submissionLockRef.current = false;
      return;
    }

    // When a ticket prompt is pending and the user declines, drop the prompt
    // without creating a ticket and acknowledge locally (no chat API call).
    if (escalationPrompt && isTicketCancellation(text)) {
      cancelTicketPrompt();
      submissionLockRef.current = false;
      return;
    }

    setIsTyping(true);
    setShowTyping(false);
    setTicketStatus({ kind: 'idle' });
    scheduleTypingIndicator();

    // A simple first greeting is answered locally — no DeepSeek call.
    if (messages.length === 0 && isSimpleGreeting(text)) {
      scheduleGreetingReply();
      return;
    }

    void requestReply([...messages, userMessage]);
  };

  const handleRetry = () => {
    const history = failedMessagesRef.current;
    if (!history || isTyping || isRateLimited || submissionLockRef.current) return;
    submissionLockRef.current = true;

    setErrorMessage(null);
    setIsTyping(true);
    setShowTyping(false);
    scheduleTypingIndicator();
    void requestReply(history);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    send(draft);
  };

  return (
    <>
      {isOpen ? (
        <div
          role="dialog"
          aria-label="Support Chat"
          className="fixed bottom-24 right-4 z-[10000] flex w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-900 sm:right-6"
        >
          <div className="flex items-center justify-between gap-3 bg-blue-600 px-4 py-3 text-white dark:bg-blue-700">
            <div className="flex min-w-0 items-center gap-3.5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden">
                <img src={tenantLogoSrc} alt="" className="h-[50px] w-[50px] object-contain" />
              </div>
              <div className="min-w-0">
                <div className="text-[18px] font-medium leading-relaxed">Support Chat</div>
                <div className="flex items-center gap-1.5 text-[11px] text-blue-100">
                  <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                  <span className="truncate">Online Assistant</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close Support Chat"
              onClick={() => setIsOpen(false)}
              className="shrink-0 rounded-lg p-1.5 text-blue-50 transition-colors hover:bg-white/15 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div
            ref={listRef}
            data-testid="ai-chat-messages"
            className="max-h-[min(46vh,340px)] min-h-[220px] space-y-3 overflow-y-auto px-4 py-4 [scrollbar-width:thin] [scrollbar-color:hsl(var(--scrollbar-thumb))_hsl(var(--scrollbar-track))] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[hsl(var(--scrollbar-track))] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[hsl(var(--scrollbar-thumb))] [&::-webkit-scrollbar-thumb]:hover:bg-[hsl(var(--scrollbar-thumb-hover))]"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'items-center'}`}
              >
                {message.role === 'assistant' ? (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden text-blue-700 dark:text-blue-300">
                    <img src={tenantLogoSrc} alt="" className="h-8 w-8 object-contain" />
                  </div>
                ) : null}
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-6 ${
                    message.role === 'user'
                      ? 'rounded-br-md bg-blue-600 text-white dark:bg-blue-700'
                      : 'rounded-bl-md border border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-300'
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
            {showTyping ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:120ms]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:240ms]" />
                </span>
                Assistant is typing…
              </div>
            ) : null}
            {errorMessage ? (
              <div
                role="alert"
                className="flex flex-col items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/60 dark:bg-red-950/40"
              >
                <span className="text-xs leading-5 text-red-700 dark:text-red-300">{errorMessage}</span>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="rounded-lg border border-red-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-700 transition-colors hover:bg-red-50 dark:border-red-800 dark:bg-zinc-900 dark:text-red-300 dark:hover:bg-red-950/60"
                >
                  Try again
                </button>
              </div>
            ) : null}
            {escalationPrompt ? (
              <div className="flex flex-col items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900/60 dark:bg-blue-950/40">
                <span className="text-xs leading-5 text-blue-900 dark:text-blue-200">
                  I think someone from Support should take a look at this. Would you like me to create a support ticket?
                </span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void handleCreateTicket()}
                    disabled={ticketStatus.kind === 'creating'}
                    className="rounded-lg border border-blue-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:border-blue-800 dark:bg-zinc-900 dark:text-blue-300 dark:hover:bg-blue-950/60"
                  >
                    {ticketStatus.kind === 'creating' ? 'Creating…' : 'Create ticket'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelTicketPrompt}
                    disabled={ticketStatus.kind === 'creating'}
                    className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                </div>
                {ticketStatus.kind === 'error' ? (
                  <span className="text-[11px] text-red-700 dark:text-red-300">
                    Sorry, we couldn&apos;t create the ticket. Please try again.
                  </span>
                ) : null}
              </div>
            ) : null}
            {ticketStatus.kind === 'created' ? (
              <div
                role="status"
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 dark:border-emerald-900/60 dark:bg-emerald-950/40"
              >
                <span className="text-xs leading-5 text-emerald-800 dark:text-emerald-200">
                  Your support ticket has been created. Ticket #{formatShortTicketRef(ticketStatus.ticketId)}.
                </span>
              </div>
            ) : null}
          </div>

          <div
            aria-hidden={messages.length > 0}
            className={`grid grid-cols-2 gap-1.5 overflow-hidden px-4 transition-[max-height,opacity,margin] duration-300 ease-out ${
              messages.length > 0 ? 'pointer-events-none max-h-0 opacity-0' : 'mt-3 max-h-20 pb-2 opacity-100'
            }`}
          >
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => send(prompt)}
                disabled={isTyping || isRateLimited}
                className="rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-600 transition-colors hover:border-blue-300 hover:text-blue-700 dark:border-zinc-700 dark:bg-zinc-950/40 dark:text-zinc-400 dark:hover:border-blue-500/60 dark:hover:text-blue-300"
              >
                {prompt}
              </button>
            ))}
          </div>

          <form
            onSubmit={onSubmit}
            className="border-t border-zinc-200 bg-zinc-50 px-3 py-3 dark:border-zinc-800 dark:bg-zinc-950/40"
          >
            {isRateLimited ? <div role="status" className="mb-2 text-xs text-zinc-500 dark:text-zinc-400">{RATE_LIMIT_MESSAGE}</div> : null}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Ask a question…"
                aria-label="Message to Support Chat"
                disabled={isRateLimited}
                className="h-10 min-w-0 flex-1 rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500"
              />
              <button
                type="submit"
                aria-label="Send message"
                disabled={!draft.trim() || isTyping || isRateLimited}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-blue-700 dark:hover:bg-blue-600"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <button
        type="button"
        aria-label="Open Support Chat"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className="fixed bottom-6 right-4 z-[10000] flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:bg-blue-700 dark:hover:bg-blue-600 sm:right-6"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </>
  );
}
